import get from 'lodash/fp/get'
import { getFieldIdBySeasonId } from './seasons'
import pipe from 'lodash/fp/pipe'
import find from 'lodash/fp/find'
import map from 'lodash/fp/map'
import { getSelectedFlightGroupFlights, getTemporalComparisonFlightGroupFlights } from './flightGroups'
import reduce from 'lodash/fp/reduce'
import { getFieldTileOrderByProvider } from '@layers-frontend/commons/store/selectors/fieldsTileOrder'
import moment from 'moment'
import { getSelectedFieldsIds } from './fields'
import size from 'lodash/size'
import union from 'lodash/union'
import { TILE_PROVIDERS } from '@layers-frontend/commons/constants'

// Generic selector, works with any provider
export const getSeasonTileOrdersSubscriptionByProvider = (seasonId, from, to, providerName) => state => {
  const fieldId = getFieldIdBySeasonId(seasonId)(state)
  const fieldTileOrders = getFieldTileOrderByProvider(fieldId, providerName)(state)

  const activeFieldTileOrder = find(
    fieldTileOrder => new Date(fieldTileOrder?.startDate) <= new Date(to) && new Date(from) <= new Date(fieldTileOrder?.endDate)
  )(fieldTileOrders)

  return activeFieldTileOrder?.tileSubscription || null
}

// Planet-specific selector
export const getPlanetSeasonTileOrdersSubscriptionByDateRange = (seasonId, from, to) => state =>
  getSeasonTileOrdersSubscriptionByProvider(seasonId, from, to, TILE_PROVIDERS.PLANET)(state)

// Planet-specific selector for instance ID
export const getPlanetSeasonTileOrdersInstanceIdByDateRange = (seasonId, from, to) => state => {
  return pipe(getPlanetSeasonTileOrdersSubscriptionByDateRange(seasonId, from, to), get('instanceId'))(state)
}

// S2DR-specific selector
export const getS2drSeasonTileOrdersSubscriptionByDateRange = (seasonId, from, to) => state =>
  getSeasonTileOrdersSubscriptionByProvider(seasonId, from, to, TILE_PROVIDERS.S2DR)(state)

// S2DR-specific selector for instance ID
export const getS2drSeasonTileOrdersInstanceIdByDateRange = (seasonId, from, to) => state => {
  return pipe(getS2drSeasonTileOrdersSubscriptionByDateRange(seasonId, from, to), get('instanceId'))(state)
}

export const getPlanetApiKeys = state => {
  const selectedFlightGroupFlights = getSelectedFlightGroupFlights(state)
  const temporalComparisonFlightGroupFlights = getTemporalComparisonFlightGroupFlights(state)
  const flights = union(selectedFlightGroupFlights, temporalComparisonFlightGroupFlights)
  return reduce((accumulator, flight) => {
    const flightSeasons = get('seasons')(flight)
    const flightSeasonIds = map(season => get('id')(season))(flightSeasons)

    const date = get('date')(flight)

    const seasonKeys = reduce((seasons, seasonId) => {
      const seasonKey = getPlanetSeasonTileOrdersInstanceIdByDateRange(seasonId, date, date)(state)
      return seasonKey ? { ...seasons, [seasonId]: seasonKey } : seasons
    }, accumulator)(flightSeasonIds)
    return seasonKeys
  }, {})(flights)
}

export const getS2drApiKeys = state => {
  const selectedFlightGroupFlights = getSelectedFlightGroupFlights(state)
  const temporalComparisonFlightGroupFlights = getTemporalComparisonFlightGroupFlights(state)
  const flights = union(selectedFlightGroupFlights, temporalComparisonFlightGroupFlights)
  return reduce((accumulator, flight) => {
    const flightSeasons = get('seasons')(flight)
    const flightSeasonIds = map(season => get('id')(season))(flightSeasons)

    const date = get('date')(flight)

    const seasonKeys = reduce((seasons, seasonId) => {
      const seasonKey = getS2drSeasonTileOrdersInstanceIdByDateRange(seasonId, date, date)(state)
      return seasonKey ? { ...seasons, [seasonId]: seasonKey } : seasons
    }, accumulator)(flightSeasonIds)
    return seasonKeys
  }, {})(flights)
}

// Generic selector, works with any provider
export const getSelectedFieldsTileOrdersActiveSubscriptionByProvider = providerName => state => {
  const fieldIds = getSelectedFieldsIds(state)
  const today = new Date()

  return reduce((accumulator, fieldId) => {
    const fieldTileOrders = getFieldTileOrderByProvider(fieldId, providerName)(state)

    const activeTileOrder = find(fieldTileOrder => {
      const subscription = get('tileSubscription')(fieldTileOrder)
      return moment(today).isSameOrAfter(subscription.startDate, 'day') && moment(today).isSameOrBefore(moment(subscription.endDate), 'day')
    })(fieldTileOrders)

    if (activeTileOrder) return [...accumulator, activeTileOrder]
    return accumulator
  }, [])(fieldIds)
}

// Planet-specific selector
export const getPlanetSelectedFieldsTileOrdersActiveSubscription = state =>
  getSelectedFieldsTileOrdersActiveSubscriptionByProvider(TILE_PROVIDERS.PLANET)(state)

// S2DR-specific selector
export const getS2drSelectedFieldsTileOrdersActiveSubscription = state => getSelectedFieldsTileOrdersActiveSubscriptionByProvider(TILE_PROVIDERS.S2DR)(state)

// Generic selector, works with any provider
export const hasSelectedFieldTileOrderSubscriptionByProvider = providerName =>
  pipe(getSelectedFieldsTileOrdersActiveSubscriptionByProvider(providerName), size, Boolean)

// Planet-specific selector
export const hasSelectedFieldPlanetTileOrderSubscription = state => hasSelectedFieldTileOrderSubscriptionByProvider(TILE_PROVIDERS.PLANET)(state)

// S2DR-specific selector
export const hasSelectedFieldS2drTileOrderSubscription = state => hasSelectedFieldTileOrderSubscriptionByProvider(TILE_PROVIDERS.S2DR)(state)
